import type { UnknownAction } from '@reduxjs/toolkit';
import { env } from '@/config/env';
import {
  isSessionExpired,
  loadPersistedSession,
  selectSession,
  sessionExpired,
  sessionRestored,
  sessionStarted,
  storedPersistence,
  toSession,
  type StateWithSession,
} from '@/shared/session';
import type { AuthResponse } from './dto';

/** `refreshed`: a new access token is in the store; `ended`: the session is over; `unavailable`: try later. */
export type RefreshOutcome = 'refreshed' | 'ended' | 'unavailable';

interface StoreAccess {
  dispatch: (action: UnknownAction) => unknown;
  getState: () => unknown;
}

/** Why the last renewal was `unavailable`: the refresh endpoint's HTTP status, or null when unreachable. */
export interface RefreshFailure {
  status: number | null;
}

const LOCK_NAME = 'oaa-bi.session-refresh';
let inFlight: Promise<RefreshOutcome> | null = null;
let failure: RefreshFailure | null = null;

/** The reason of the last `unavailable` renewal in this tab; cleared once a renewal succeeds. */
export function lastRefreshFailure(): RefreshFailure | null {
  return failure;
}

/**
 * Renews the access token with the HttpOnly refresh cookie set at sign-in. Concurrent callers in a
 * tab share one request, and tabs take turns (Web Locks), so a tab that waited adopts the token
 * another tab just stored instead of renewing again.
 */
export function refreshSession(store: StoreAccess): Promise<RefreshOutcome> {
  inFlight ??= withLock(() => renew(store))
    .then((outcome) => {
      if (outcome !== 'unavailable') failure = null;
      return outcome;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Removes the refresh cookie, so this browser cannot renew the session any more. */
export function endServerSession(): void {
  fetch(`${env.apiBaseUrl}/api/auth/logout`, { method: 'POST', credentials: 'same-origin' }).catch(
    () => {
      // Offline: the cookie stays until its own expiry, but no tab holds a session to renew.
    },
  );
}

function withLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? locks.request(LOCK_NAME, task) : task();
}

async function renew({ dispatch, getState }: StoreAccess): Promise<RefreshOutcome> {
  const current = selectSession(getState() as StateWithSession);
  if (!current) return 'ended';

  const stored = loadPersistedSession();
  if (stored && stored.accessToken !== current.accessToken && !isSessionExpired(stored)) {
    dispatch(sessionRestored(stored));
    return 'refreshed';
  }

  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json, application/problem+json' },
      signal: AbortSignal.timeout(env.apiTimeoutMs),
    });
  } catch {
    failure = { status: null };
    return 'unavailable';
  }

  // Signed out (or signed in again) while the request ran: leave the new state alone.
  const now = selectSession(getState() as StateWithSession);
  if (now !== current) return now ? 'refreshed' : 'ended';

  if (response.status === 401) {
    dispatch(sessionExpired());
    return 'ended';
  }
  if (!response.ok) {
    failure = { status: response.status };
    return 'unavailable';
  }

  try {
    const data = (await response.json()) as AuthResponse;
    dispatch(sessionStarted({ session: toSession(data), persistence: storedPersistence() }));
    return 'refreshed';
  } catch {
    failure = { status: response.status };
    return 'unavailable';
  }
}
